import {RootRoute} from '../../routes/RootRoute';

/**
 * Validates if a user has permission to access the given return URL
 * @param returnUrl - The URL the user wants to return to
 * @param isAdmin - Whether the user has admin permissions
 * @returns true if user has permission, false otherwise
 */
export const validateReturnUrlPermissions = (
  returnUrl: string,
  isAdmin: boolean,
): boolean => {
  // If no return URL, it's valid (will use default routing)
  if (!returnUrl) {
    return true;
  }

  // Check if return URL is an admin-only route
  const isAdminOnlyRoute = returnUrl.startsWith(RootRoute.AuthAdminHome);

  // Check if return URL is an event route (accessible to all authenticated users)
  const isEventRoute =
    returnUrl.startsWith('/employee/event') || returnUrl.startsWith('/events');

  // Check if return URL is an employee-only route
  const isEmployeeOnlyRoute =
    returnUrl.startsWith(RootRoute.EmployeeHome) ||
    returnUrl.startsWith(RootRoute.EmployeePastEvents);

  // Admin users can access admin routes and shared routes (events), but NOT employee routes
  if (isAdmin) {
    if (isEmployeeOnlyRoute) {
      return false; // Admins cannot access employee routes
    }
    return true; // Admins can access admin routes and shared routes
  }

  // Non-admin users cannot access admin-only routes
  if (isAdminOnlyRoute) {
    return false;
  }

  // Non-admin users can access employee routes and shared routes (events)
  if (isEmployeeOnlyRoute || isEventRoute) {
    return true;
  }

  // For any other routes, allow access (like public routes)
  return true;
};

/**
 * Gets the appropriate fallback route for a user when return URL is invalid
 * @param isAdmin - Whether the user has admin permissions
 * @param isAdminView - Whether the admin is currently in admin view (only relevant for admins)
 * @returns The appropriate route to redirect to
 */
export const getFallbackRoute = (
  isAdmin: boolean,
  isAdminView?: boolean,
): string => {
  if (isAdmin && isAdminView !== false) {
    return RootRoute.AuthAdminHome;
  }
  return RootRoute.EmployeeHome;
};
